/**
 * 🔐 Verification Requirements Management Page
 * Manage verification tier rules and configurations
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Chip,
  Stack,
  IconButton,
  Tooltip,
  Grid,
  Divider,
  Paper,
  ToggleButton,
  ToggleButtonGroup,
  Dialog,
  DialogContent,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  History,
  MonetizationOn,
  DirectionsCar,
  Security,
  CheckCircle,
  Cancel,
  SwapVert,
  ArrowUpward,
  ArrowDownward,
  Save,
} from '@mui/icons-material';
import {
  adminVerificationRequirementsService,
  VerificationRequirement,
} from '@/services/adminVerificationRequirementsService';
import { VerificationRequirementForm } from '@/components/admin/VerificationRequirementForm';
import { VerificationRequirementsAuditLogPage } from './VerificationRequirementsAuditLog';
import ConfirmDialog from '@/components/common/ConfirmDialog';
import toast from 'react-hot-toast';

export const VerificationRequirementsPage: React.FC = () => {
  const [requirements, setRequirements] = useState<VerificationRequirement[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'active' | 'inactive'>('active');
  const [formOpen, setFormOpen] = useState(false);
  const [auditLogOpen, setAuditLogOpen] = useState(false);
  const [reorderMode, setReorderMode] = useState(false);
  const [reordering, setReordering] = useState(false);
  const [editingRequirement, setEditingRequirement] = useState<VerificationRequirement | undefined>(undefined);

  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: '',
    message: '',
    onConfirm: () => {},
    severity: 'warning' as 'warning' | 'error' | 'info' | 'question',
  });

  useEffect(() => {
    loadRequirements();
  }, [filter]);

  const loadRequirements = async () => {
    setLoading(true);
    try {
      const filterValue = filter === 'all' ? undefined : filter === 'active';
      const response = await adminVerificationRequirementsService.listVerificationRequirements(filterValue);

      if (response.success) {
        // Sort by priority (highest first)
        const sorted = [...response.data].sort((a, b) => b.priority - a.priority);
        setRequirements(sorted);
      } else {
        toast.error('Failed to load verification requirements');
      }
    } catch (error) {
      console.error('Failed to load verification requirements:', error);
      toast.error('Failed to load verification requirements');
    } finally {
      setLoading(false);
    }
  };

  const handleDeactivate = async (requirement: VerificationRequirement) => {
    setConfirmDialog({
      open: true,
      title: 'Deactivate Verification Requirement',
      message: `Are you sure you want to deactivate "${requirement.rule_name}"? This will prevent it from being applied to new bookings.`,
      severity: 'warning',
      onConfirm: async () => {
        try {
          const response = await adminVerificationRequirementsService.deactivateVerificationRequirement(requirement.id);

          if (response.success) {
            toast.success('Verification requirement deactivated');
            loadRequirements();
          } else {
            toast.error(response.message || 'Failed to deactivate requirement');
          }
        } catch (error: any) {
          console.error('Failed to deactivate requirement:', error);
          const errorMessage = error.response?.data?.message || 'Failed to deactivate requirement';
          toast.error(errorMessage);
        } finally {
          setConfirmDialog(prev => ({ ...prev, open: false }));
        }
      },
    });
  };

  const handleReactivate = async (id: string) => {
    try {
      const response = await adminVerificationRequirementsService.activateVerificationRequirement(id);

      if (response.success) {
        toast.success('Verification requirement activated');
        loadRequirements();
      } else {
        toast.error('Failed to activate requirement');
      }
    } catch (error) {
      console.error('Failed to activate requirement:', error);
      toast.error('Failed to activate requirement');
    }
  };

  const handleDelete = async (requirement: VerificationRequirement) => {
    setConfirmDialog({
      open: true,
      title: 'Delete Verification Requirement',
      message: `Are you sure you want to PERMANENTLY delete "${requirement.rule_name}"? This action cannot be undone and will remove all audit history.`,
      severity: 'error',
      onConfirm: async () => {
        try {
          const response = await adminVerificationRequirementsService.deleteVerificationRequirement(requirement.id);

          if (response.success) {
            toast.success('Verification requirement deleted permanently');
            loadRequirements();
          } else {
            toast.error(response.message || 'Failed to delete requirement');
          }
        } catch (error: any) {
          console.error('Failed to delete requirement:', error);
          const errorMessage = error.response?.data?.message || 'Failed to delete requirement';
          toast.error(errorMessage);
        } finally {
          setConfirmDialog(prev => ({ ...prev, open: false }));
        }
      },
    });
  };

  const handleMoveUp = (index: number) => {
    if (index === 0) return;
    const newRequirements = [...requirements];
    [newRequirements[index - 1], newRequirements[index]] = [newRequirements[index], newRequirements[index - 1]];
    setRequirements(newRequirements);
  };

  const handleMoveDown = (index: number) => {
    if (index === requirements.length - 1) return;
    const newRequirements = [...requirements];
    [newRequirements[index], newRequirements[index + 1]] = [newRequirements[index + 1], newRequirements[index]];
    setRequirements(newRequirements);
  };

  const handleSaveReorder = async () => {
    setConfirmDialog({
      open: true,
      title: 'Save Priority Order',
      message: 'Are you sure you want to save the new priority order? This will affect which verification requirements are applied first.',
      severity: 'question',
      onConfirm: async () => {
        setReordering(true);
        try {
          // Create reorder payload with new priorities (highest to lowest)
          const reorderPayload = requirements.map((req, index) => ({
            id: req.id,
            priority: requirements.length - index, // Reverse order so first item has highest priority
          }));

          const response = await adminVerificationRequirementsService.reorderVerificationRequirements(reorderPayload);

          if (response.success) {
            toast.success('Priority order updated successfully');
            setReorderMode(false);
            loadRequirements();
          } else {
            toast.error(response.message || 'Failed to reorder requirements');
          }
        } catch (error: any) {
          console.error('Failed to reorder requirements:', error);
          const errorMessage = error.response?.data?.message || 'Failed to reorder requirements';
          toast.error(errorMessage);
        } finally {
          setReordering(false);
          setConfirmDialog(prev => ({ ...prev, open: false }));
        }
      },
    });
  };

  const handleCancelReorder = () => {
    setReorderMode(false);
    loadRequirements(); // Reload to reset order
  };

  const formatCurrency = (amount: number | null) => {
    if (amount === null) return 'Unlimited';
    return `₦${amount.toLocaleString()}`;
  };

  const formatVehicleTiers = (tiers: string[] | null) => {
    if (!tiers || tiers.length === 0) return 'All';
    return tiers.join(', ');
  };

  const formatServiceTypes = (types: string[] | null) => {
    if (!types || types.length === 0) return 'All';
    return types.join(', ');
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
          🔐 Verification Requirements
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Manage verification tier rules and their configurations
        </Typography>
      </Box>

      {/* Action Bar */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <ToggleButtonGroup
          value={filter}
          exclusive
          onChange={(e, newFilter) => newFilter && setFilter(newFilter)}
          size="small"
        >
          <ToggleButton value="active">Active</ToggleButton>
          <ToggleButton value="inactive">Inactive</ToggleButton>
          <ToggleButton value="all">All</ToggleButton>
        </ToggleButtonGroup>

        <Stack direction="row" spacing={2}>
          {reorderMode ? (
            <>
              <Button
                variant="outlined"
                onClick={handleCancelReorder}
                disabled={reordering}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                color="success"
                startIcon={reordering ? <CircularProgress size={16} /> : <Save />}
                onClick={handleSaveReorder}
                disabled={reordering}
              >
                {reordering ? 'Saving...' : 'Save Order'}
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="outlined"
                startIcon={<SwapVert />}
                onClick={() => setReorderMode(true)}
              >
                Reorder Priorities
              </Button>
              <Button
                variant="outlined"
                startIcon={<History />}
                onClick={() => setAuditLogOpen(true)}
              >
                View Audit Log
              </Button>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={() => {
                  setEditingRequirement(undefined);
                  setFormOpen(true);
                }}
              >
                Create New Rule
              </Button>
            </>
          )}
        </Stack>
      </Box>

      {/* Requirements List */}
      {requirements.length === 0 ? (
        <Alert severity="info">
          No verification requirements found. Create one to get started.
        </Alert>
      ) : (
        <Grid container spacing={3}>
          {requirements.map((requirement, index) => (
            <Grid item xs={12} key={requirement.id}>
              <Card
                sx={{
                  p: 3,
                  opacity: requirement.is_active ? 1 : 0.6,
                  border: requirement.is_active ? '2px solid' : '1px solid',
                  borderColor: requirement.is_active ? 'primary.main' : 'divider',
                }}
              >
                {/* Header */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                  <Box>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {requirement.rule_name.replace(/_/g, ' ').toUpperCase()}
                      </Typography>
                      <Chip
                        label={`Priority ${requirement.priority}`}
                        size="small"
                        color={requirement.priority >= 3 ? 'primary' : 'default'}
                      />
                      {!requirement.is_active && (
                        <Chip label="Inactive" size="small" color="error" />
                      )}
                    </Stack>
                  </Box>

                  <Stack direction="row" spacing={1}>
                    {reorderMode ? (
                      <>
                        <Tooltip title="Move Up">
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => handleMoveUp(index)}
                              disabled={index === 0}
                            >
                              <ArrowUpward fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Move Down">
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => handleMoveDown(index)}
                              disabled={index === requirements.length - 1}
                            >
                              <ArrowDownward fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </>
                    ) : (
                      <>
                        <Tooltip title="Edit">
                          <IconButton
                            size="small"
                            onClick={() => {
                              setEditingRequirement(requirement);
                              setFormOpen(true);
                            }}
                          >
                            <Edit fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {requirement.is_active ? (
                          <Tooltip title="Deactivate">
                            <IconButton
                              size="small"
                              color="warning"
                              onClick={() => handleDeactivate(requirement)}
                            >
                              <Cancel fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        ) : (
                          <Tooltip title="Activate">
                            <IconButton
                              size="small"
                              color="success"
                              onClick={() => handleReactivate(requirement.id)}
                            >
                              <CheckCircle fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="Delete Permanently">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDelete(requirement)}
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                  </Stack>
                </Box>

                <Divider sx={{ mb: 2 }} />

                {/* Details Grid */}
                <Grid container spacing={2}>
                  {/* Verification Requirements */}
                  <Grid item xs={12} md={6}>
                    <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
                      <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                        Required Verifications
                      </Typography>
                      <Stack spacing={0.5}>
                        {requirement.phone_verified && (
                          <Chip icon={<CheckCircle />} label="Phone Verified" size="small" color="success" variant="outlined" />
                        )}
                        {requirement.driver_license_verified && (
                          <Chip icon={<CheckCircle />} label="Driver's License" size="small" color="success" variant="outlined" />
                        )}
                        {requirement.identity_verified && (
                          <Chip icon={<CheckCircle />} label="Identity Verified" size="small" color="success" variant="outlined" />
                        )}
                        {requirement.is_verified && (
                          <Chip icon={<CheckCircle />} label="Admin Approved" size="small" color="success" variant="outlined" />
                        )}
                        {!requirement.phone_verified && !requirement.driver_license_verified &&
                         !requirement.identity_verified && !requirement.is_verified && (
                          <Typography variant="body2" color="text.secondary">
                            No specific verifications required
                          </Typography>
                        )}
                      </Stack>
                    </Paper>
                  </Grid>

                  {/* Booking Limits */}
                  <Grid item xs={12} md={6}>
                    <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
                      <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                        <MonetizationOn fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                        Booking Limits
                      </Typography>
                      <Stack spacing={1}>
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Max Booking Amount
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {formatCurrency(requirement.max_booking_amount)}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Min Security Deposit
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {formatCurrency(requirement.min_security_deposit)}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Security Deposit Rate
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {requirement.security_deposit_rate ?? 10}%
                          </Typography>
                        </Box>
                      </Stack>
                    </Paper>
                  </Grid>

                  {/* Vehicle & Service Restrictions */}
                  <Grid item xs={12} md={6}>
                    <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
                      <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                        <DirectionsCar fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                        Vehicle Tiers
                      </Typography>
                      <Typography variant="body2">
                        {formatVehicleTiers(requirement.allowed_vehicle_tiers)}
                      </Typography>
                    </Paper>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
                      <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                        <Security fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                        Service Types
                      </Typography>
                      <Typography variant="body2">
                        {formatServiceTypes(requirement.allowed_service_types)}
                      </Typography>
                    </Paper>
                  </Grid>

                  {/* Permissions */}
                  {(requirement.can_be_host || requirement.can_be_driver) && (
                    <Grid item xs={12}>
                      <Paper variant="outlined" sx={{ p: 2 }}>
                        <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                          Special Permissions
                        </Typography>
                        <Stack direction="row" spacing={1}>
                          {requirement.can_be_host && (
                            <Chip label="Can Be Host" size="small" color="primary" />
                          )}
                          {requirement.can_be_driver && (
                            <Chip label="Can Be Driver" size="small" color="primary" />
                          )}
                        </Stack>
                      </Paper>
                    </Grid>
                  )}
                </Grid>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Form Dialog */}
      <Dialog
        open={formOpen}
        onClose={() => setFormOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogContent>
          <VerificationRequirementForm
            requirement={editingRequirement}
            onSuccess={() => {
              setFormOpen(false);
              loadRequirements();
            }}
            onCancel={() => setFormOpen(false)}
          />
        </DialogContent>
      </Dialog>

      {/* Audit Log Dialog */}
      <Dialog
        open={auditLogOpen}
        onClose={() => setAuditLogOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogContent sx={{ p: 0 }}>
          <VerificationRequirementsAuditLogPage onBack={() => setAuditLogOpen(false)} />
        </DialogContent>
      </Dialog>

      {/* Confirmation Dialog */}
      <ConfirmDialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog(prev => ({ ...prev, open: false }))}
        onConfirm={confirmDialog.onConfirm}
        title={confirmDialog.title}
        message={confirmDialog.message}
        severity={confirmDialog.severity}
        confirmText="Confirm"
        cancelText="Cancel"
      />
    </Box>
  );
};

export default VerificationRequirementsPage;
